import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  updateAdminUserSettings,
  addAdminUserAddons,
  removeAdminUserAddons,
  updateAdminUserAddonSettings,
  uploadAdminUserWatermark,
} from '../functions/adminUserSettingsApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { adminUserSettingsQueryKey } from '../queries/adminUserSettingsQueries';

export function useUpdateAdminUserSettingsMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, { key: string; value: any }>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((data: { key: string; value: any }) => updateAdminUserSettings(userId, data, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries(adminUserSettingsQueryKey(userId));
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useAddAdminUserAddonsMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, Record<string, { enabled: boolean; added: boolean }>>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () =>
      queryFnFetchErrorWrapper((data: Record<string, { enabled: boolean; added: boolean }>) =>
        addAdminUserAddons(userId, data, accessToken),
      ),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries(adminUserSettingsQueryKey(userId));
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useRemoveAdminUserAddonsMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, string[]>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((addonKeys: string[]) => removeAdminUserAddons(userId, addonKeys, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries(adminUserSettingsQueryKey(userId));
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useUpdateAdminUserAddonSettingsMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, { addonKey: string; data: any }>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = useMemo(
    () =>
      queryFnFetchErrorWrapper((params: { addonKey: string; data: any }) =>
        updateAdminUserAddonSettings(userId, params.addonKey, params.data, accessToken),
      ),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries(adminUserSettingsQueryKey(userId));
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useUploadAdminUserWatermarkMutation(
  userId: number,
  mutationOptions?: UseMutationOptions<any, FetchError, File>,
) {
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((file: File) => uploadAdminUserWatermark(userId, file, accessToken)),
    [userId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
  });
}
